import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from "react-redux";
import VerificationModal from './VerificationModal';
import { Box, IconButton, Modal, Pagination } from '@mui/material';
import { formatDate } from '../../utils/dateTime';
import Loader from '../common/Loader/Loader';
import SuspendCard from '../user-management/SuspendCard';
import CloseIcon from '@mui/icons-material/Close';
import ProfileCard from '../user-management/ProfileCard';
import { style2 } from '../user-management/UserList';
import UserTableSkeleton from '../user-management/UserTableSkeleton';
import Tooltip from '@mui/material/Tooltip';
import { setSearchTermAction } from '../../containers/app-container/reducer/filterSlice';
import { initColumns, toggleColumn, updateMultipleColumns } from '../../containers/app-container/reducer/visibleColumnsSlice';
import { statusLabel } from '../../utils/memberStatus';

// Status pill: the server's `statusBucket` (getUsers), coloured with the pill styles this
// table already had. The suspend/active rule is only the rollout fallback for an older server.
const STATUS_PILL = {
  approved: { backgroundColor: 'var(--success-600) !important', color: '#fff' },
  hidden: { backgroundColor: '#fff3cd', color: '#856404' },
  deactivated: { backgroundColor: '#fff3cd', color: '#856404' },
};
const DANGER_PILL = { backgroundColor: '#f8d7da', color: '#721c24' };
const getStatusPill = (item) => {
  if (item?.statusBucket) {
    return { label: statusLabel(item.statusBucket), style: STATUS_PILL[item.statusBucket] || DANGER_PILL };
  }
  return legacyStatusPill(item);
};
// Rollout fallback for a server that does not send statusBucket yet — remove once it does.
const legacyStatusPill = (item) => ({
  label: item?.suspend ? 'Suspend' : (item?.active ? 'Active' : 'Deactivated'),
  style: {
    backgroundColor: item?.active ? 'var(--success-600) !important' : '#fff3cd',
    color: item?.active ? '#fff' : '#856404',
  },
});
const visibleColumnKey = [
  "email",
  "age",
  "gender",
  "status",
  "idVerification",
  "selfieVerification",
  "idAttachment",
  "selfieAttachment",
  "location",
  "username",
  "subscriptionStatus",
  "registrationDate"
]

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 600,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: 4,
  maxHeight: '80vh', // Adjust the height for scrolling
  overflow: 'auto', // Make content scrollable
};

function VerificationTable({
  data,
  totalPages,
  currentPage,
  handlePageChange,
  itemsPerPage,
  loading,
  searchTerm,
  setSearchTerm,
  setCurrentPage,
  handleSearch,
  fetchData,
  setActiveFilter,
  activeFilter,
}) {
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const searchTermFromStore = useSelector((state) => state.filter.searchTerm);
  const visibleColumns = useSelector(state => state.visibleColumns.pages.verification || {});
  const [open, setOpen] = useState(false);
  const [selectedVerificationType, setSelectedVerificationType] = useState(null);
  const [showRejectReasonsModal, setShowRejectReasonsModal] = useState(false);
  const [showVerificationViewer, setShowVerificationViewer] = useState(true);
  const [selectedUser, setSelectedUser] = useState(false);
  const [openSuspend, setOpenSuspend] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const filter = useSelector((state) => state.filter);

  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });
  // Keep a local searchTerm to control input immediately
  // const [searchTerm, setSearchTermLocal] = useState(parentSearchTerm ?? '');
  const dispatch = useDispatch();
  // When parent updates the searchTerm (from store or other), sync local input
  // useEffect(() => {
  //   if ((parentSearchTerm ?? '') !== (searchTerm ?? '')) {
  //     setSearchTermLocal(parentSearchTerm ?? '');
  //     setCurrentPage && setCurrentPage(1);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [parentSearchTerm]);
  // Scroll thumb width calculation
  const thumbWidth = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.clientWidth / scrollState.scrollWidth;
      return Math.max(ratio * scrollState.clientWidth, 50);
    }
    return 100;
  }, [scrollState]);

  // Scroll thumb position
  const thumbPosition = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
      return ratio * (scrollState.clientWidth - thumbWidth);
    }
    return 0;
  }, [scrollState, thumbWidth]);

  const updateScrollState = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({
        scrollLeft,
        scrollWidth,
        clientWidth,
      });
    }
  };

  const handleTableScroll = () => {
    updateScrollState();
  };

  const handleStickyScroll = (e) => {
    if (!tableContainerRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const thumbCenter = thumbWidth / 2;

    const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) *
      (scrollState.scrollWidth - scrollState.clientWidth);

    tableContainerRef.current.scrollLeft = newScrollLeft;
  };

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = scrollState.scrollLeft;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const trackWidth = scrollState.clientWidth - thumbWidth;
      const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

      if (trackWidth > 0) {
        const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // seEffect visibleColumns  
  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('resize', updateScrollState);
    };
  }, [data, visibleColumns]);

  const handleOpen = (verificationType, user) => {
    setSelectedVerificationType(verificationType);
    setOpen(true);
    setShowVerificationViewer(true);
    setSelectedUser(user);
  };

  const handleOpenSuspend = (user) => {
    setSelectedUser(user);
    setOpenSuspend(true);
  };

  const handleOpenProfile = (user) => {
    setSelectedUser(user);
    setOpenProfile(true);
  };

  const handleCloseModal = () => {
    setSelectedVerificationType(null);
    setOpen(false);
    setShowRejectReasonsModal(false);
  };

  const handleCloseSuspend = () => setOpenSuspend(false);
  const handleCloseProfile = () => setOpenProfile(false);

  const handleReject = () => {
    setShowRejectReasonsModal(true);
    setShowVerificationViewer(false);
  };

  const handleRequestReUpload = () => {
    setShowRejectReasonsModal(true);
    setShowVerificationViewer(false); // Hide verification viewer
  };



  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    const updates = {
      idVerification: filter === 'All' || filter === 'ID Verification',
      idAttachment: filter === 'All' || filter === 'ID Verification',
      selfieVerification: filter === 'All' || filter === 'Selfie Verification',
      selfieAttachment: filter === 'All' || filter === 'Selfie Verification',
    };

    dispatch(updateMultipleColumns({
      pageKey: 'verification',
      updates
    }));
  };


  const filteredData = data


  const columnHeaders = [
    { key: 'email', label: 'Email' },
    { key: 'age', label: 'Age' },
    { key: 'gender', label: 'Gender' },
    { key: 'status', label: 'Status' },
    // { key: "verificationType", label: "Verification Type" },
    { key: 'idVerification', label: 'ID Verification' },
    { key: 'selfieVerification', label: 'Selfie Verification' },
    { key: 'idAttachment', label: 'ID Attachment' },
    { key: 'selfieAttachment', label: 'Selfie Attachment' },
    // { key: 'action', label: 'Action' },
    { key: 'location', label: 'Location' },
    { key: 'username', label: 'Username' },
    { key: 'subscriptionStatus', label: 'Subscription Status' },
    { key: 'registrationDate', label: 'Registration Date' },
  ];

  const handleOpenModal = (verificationType) => {
    setSelectedVerificationType(verificationType); // Set verification type
    setOpen(true); // Open modal
  };

  // useEffect(() => {
  //   if (searchTerm == undefined || searchTerm == null) return
  //   if (debounceTimeoutRef.current) {
  //     clearTimeout(debounceTimeoutRef.current);
  //   }

  //   debounceTimeoutRef.current = setTimeout(() => {
  //     if (abortControllerRef.current) {
  //       abortControllerRef.current.abort();
  //     }
  //     const controller = new AbortController();
  //     abortControllerRef.current = controller;

  //     fetchData(controller.signal)
  //       .catch((err) => {
  //         if (err.name !== "AbortError") {
  //           console.error("Search error:", err);
  //         }
  //       });
  //   }, 300);

  //   return () => {
  //     if (debounceTimeoutRef.current) {
  //       clearTimeout(debounceTimeoutRef.current);
  //     }
  //     if (abortControllerRef.current) {
  //       abortControllerRef.current.abort();
  //     }
  //   };
  // }, [searchTerm]);
  // When the user types in the search input:
 
  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return

    clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      dispatch(setSearchTermAction({ searchTerm }))
    }, 300);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (searchTermFromStore == null && searchTerm) setSearchTerm(null);
  }, [searchTermFromStore])
  
  const handleSearchChange = (e) => {
    const value = e.target.value ?? '';
    setSearchTerm(value);
    // // update parent (which may update store) and also dispatch to store
    // setParentSearchTerm && setParentSearchTerm(value);
    // dispatch(setSearchTermAction({ searchTerm: value }));
    // // reset page
    // setCurrentPage && setCurrentPage(1);
  };

    const handleColumnToggle = (key) => {
      dispatch(
        toggleColumn({
          pageKey: "verification",
          column: key
        }));
    };
  
    useEffect(() => {
      dispatch(
        initColumns({
          pageKey: "verification",
          columns: visibleColumnKey
        })
      );
    }, []);
  return (
    <>
      <div className="mb-0 d-flex justify-content-between align-items-center">
        <h6 className="text-lg mb-0">{filter?.statsTitle ?? "Verification Request"}</h6>
        <div className="d-flex align-items-center gap-2">
          <form className="navbar-search" onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              value={searchTerm || ""}
              autoComplete="off"
              onChange={(e) => handleSearchChange(e)}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>
          {/* <Tooltip title="Download Data" placement="top" arrow>
  <button className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light">
    <SimCardDownloadOutlinedIcon />
  </button>
</Tooltip> */}
          {/* <button className="badge text-sm fw-medium border border bg-transparent px-10 py-10 radius-6 text-secondary-light">
            <FilterListOutlinedIcon />
          </button> */}

          <div className="dropdown">
            <Tooltip title="Manage Columns" arrow placement="top">
              <button
                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <ViewColumnOutlinedIcon />
              </button>
            </Tooltip>
            <ul className="dropdown-menu manage-list">
              {columnHeaders.map((column) => (
                <li
                  key={column.key}
                  className="dropdown-item d-flex align-items-center gap-2"
                >
                  <input
                    className="form-check-input"
                    style={{ width: '18px', height: '18px' }}
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                  <label className="ms-2">{column.label}</label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="d-flex align-items-center gap-3">
        <button
          className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === 'All'
            ? 'text-warning-900 bg-warning-50'
            : 'bg-light-100 text-secondary-light'
            }`}
          onClick={() => handleFilterChange('All')}
        >
          All
        </button>
        <button
          className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === 'ID Verification'
            ? 'text-warning-900 bg-warning-50'
            : 'bg-light-100 text-secondary-light'
            }`}
          onClick={() => handleFilterChange('ID Verification')}
        >
          ID Verification
        </button>
        <button
          className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === 'Selfie Verification'
            ? 'text-warning-900 bg-warning-50'
            : 'bg-light-100 text-secondary-light'
            }`}
          onClick={() => handleFilterChange('Selfie Verification')}
        >
          Selfie Verification
        </button>
      </div>

      {/* Table */}
      <div className="table-scroll-container mt-3">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={handleTableScroll}
           style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
        >
          <table
            className="table bordered-table sm-table mb-0"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              position: 'relative',
            }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                <th>#</th>
                {visibleColumns.name && <th>Name</th>}
                {visibleColumns.email && <th>Email</th>}
                {visibleColumns.age && <th className="text-center">Age</th>}
                {visibleColumns.gender && <th className="text-center">Gender</th>}
                {visibleColumns.status && <th className="text-center">Status</th>}
                {visibleColumns.idVerification && (
                  <th className="text-center">ID Verification</th>
                )}
                {visibleColumns.idAttachment && (
                  <th className="text-center">ID Attachment</th>
                )}
                {visibleColumns.selfieVerification && (
                  <th className="text-center">Selfie Verification</th>
                )}
                {visibleColumns.selfieAttachment && (
                  <th className="text-center">Selfie Attachment</th>
                )}
                {/* {visibleColumns.action && <th className="text-center">Action</th>} */}
                {visibleColumns.location && (
                  <th className="text-center">Location</th>
                )}
                {visibleColumns.username && (
                  <th className="text-center">Username</th>
                )}
                {visibleColumns.subscriptionStatus && (
                  <th className="text-center">Subscription Status</th>
                )}
                {visibleColumns.registrationDate && (
                  <th className="text-center">Registration Date</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <UserTableSkeleton
                  rows={10}
                  columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
                />
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    {visibleColumns.name && <td>{item.name}</td>}
                    {visibleColumns.email && <td>{item.email}</td>}
                    {visibleColumns.age && (
                      <td className="text-center">{item.age}</td>
                    )}
                    {visibleColumns.gender && (
                      <td className="text-center">{item.gender}</td>
                    )}
                    {visibleColumns.status && (
                      // <td className="text-center">
                      //   <span
                      //     className="badge text-sm fw-medium rounded-pill px-20 py-9 radius-4"
                      //     style={{
                      //       backgroundColor:
                      //         item.status === "Approved"
                      //           ? "var(--success-600) !important"
                      //           : item.status === "Pending"
                      //           ? "#fff3cd"
                      //           : item.status === "InProgress"
                      //           ? "#cce5ff"
                      //           : "#f8d7da",
                      //       color:
                      //         item.status === "Approved"
                      //           ? "#fff"
                      //           : item.status === "Pending"
                      //           ? "#856404"
                      //           : item.status === "InProgress"
                      //           ? "#004085"
                      //           : "#721c24",
                      //     }}
                      //   >
                      //     {item.status}
                      //   </span>
                      // </td>
                      <td className="text-center">
                        <span
                          className="badge text-sm fw-medium rounded-pill px-20 py-9 radius-4"
                          style={getStatusPill(item).style}
                        >
                          {getStatusPill(item).label}
                        </span>
                      </td>
                    )}
                    {visibleColumns.idVerification && (
                      <td className="text-center">
                        {item?.documents?.status
                          ? item?.documents?.status
                          : 'Unverified'}
                      </td>
                    )}
                    {visibleColumns.idAttachment && (
                      <td className="text-center">
                        <Button
                         disabled={!item?.documents?.front && !item?.documents?.back}
                          onClick={() => handleOpen('ID Verification', item)}
                          className={`${(item?.documents?.front || item?.documents?.back) && "text-info-500"} fw-medium`}
                        >
                          View
                        </Button>
                      </td>
                    )}
                    {visibleColumns.selfieVerification && (
                      <td className="text-center">
                        {item?.selfieVerificationStatus
                          ? item?.selfieVerificationStatus
                          : 'Unverified'}
                      </td>
                    )}
                    {visibleColumns.selfieAttachment && (
                      <td className="text-center">
                        <Button
                        disabled={!item?.realTimePicture}
                          onClick={() => handleOpen('Selfie Verification', item)}
                          className={`${item?.realTimePicture && "text-info-500"} fw-medium`}
                        >
                          View
                        </Button>
                      </td>
                    )}
                    {/* {visibleColumns.action && (
                    <td className="text-center">
                      <div className="dropdown">
                        <button
                          className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          Action
                        </button>
                        <ul className="dropdown-menu" style={{}}>
                          <li>
                            <a
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                              href="javascript:void(0)"
                              onClick={() => handleOpenSuspend(item)}
                            >
                              Suspended
                            </a>
                          </li>
                          <li>
                            <a
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                              href="javascript:void(0)"
                              onClick={() => handleOpenProfile(item)}
                            >
                              View
                            </a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  )} */}
                    {visibleColumns.location && (
                      <td className="text-center">
                        {[item.city, item.state, item.country].filter(Boolean).join(", ")}
                      </td>
                    )}
                    {visibleColumns.username && (
                      <td className="text-center">{(item.firstName ?? item.userName)}</td>
                    )}
                    {visibleColumns.subscriptionStatus && (
                      <td className="text-center">
                        {/* {item.subscription === 'Free' && ( */}
                        {!item.isSubscribed ? (
                          <div className="freesub">Free</div>
                        ) : (
                          <div className="freesub">Subscribed</div>
                        )}
                        {/* {item.subscription === "Gold" && (
                      <div className="goldsub mx-auto">
                        <img
                          src="/assets/images/admin-panel/gold.svg"
                          className="img-fluid"
                          alt="Gold"
                        />
                        Gold
                      </div>
                    )}
                    {item.subscription === "Diamond" && (
                      <div className="Diamond">
                        <img
                          src="/assets/images/admin-panel/dimond.svg"
                          className="img-fluid"
                          alt="Diamond"
                        />
                        Diamond
                      </div>
                    )} */}
                      </td>
                    )}
                    {visibleColumns.registrationDate && (
                      <td className="text-center">
                        {item?.createdAt ? formatDate(item.createdAt) : ''}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky Scroll Bar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div
              className="sticky-scroll-track"
              onClick={handleStickyScroll}
            >
              <div
                ref={scrollThumbRef}
                className="sticky-scroll-thumb"
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbPosition}px)`
                }}
                onMouseDown={handleThumbDrag}
              />
            </div>
          </div>
        )}
      </div>

     {Boolean(totalPages) && <div className="d-flex justify-content-center mt-16">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }} 
        />
      </div>}

      <VerificationModal
        open={open}
        handleCloseModal={handleCloseModal}
        selectedVerificationType={selectedVerificationType}
        showVerificationViewer={showVerificationViewer}
        setShowVerificationViewer={setShowVerificationViewer}
        showRejectReasonsModal={showRejectReasonsModal}
        handleRequestReUpload={handleRequestReUpload}
        handleReject={handleReject}
        user={selectedUser}
        fetchData={fetchData}
      />

      <Modal
        open={openSuspend}
        onClose={handleCloseSuspend}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
            }}
            onClick={handleCloseSuspend}
          >
            <CloseIcon />
          </IconButton>
          <SuspendCard
            userId={selectedUser?._id}
            handleClose={handleCloseSuspend}
             fetchData={fetchData}
          />
        </Box>
      </Modal>

      <Modal
        open={openProfile}
        onClose={handleCloseProfile}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style2}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              borderBottom: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              sx={{
                position: 'absolute',
                top: 10,
                right: 0,
              }}
              onClick={handleCloseProfile}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <ProfileCard userId={selectedUser?._id} />
        </Box>
      </Modal>
    </>
  );
}

export default VerificationTable;